import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { State } from '@/types';
import { emptyStringValues } from '@/utils/objects';

import { ChannelRecordingType } from '../types';

import { channelRecordingsQueryKeys } from './query-keys';

export type TPostChannelRecordingSchedule = {
  repetition_day_of_week?: string | null;
  start_hour: string;
  end_hour: string;
};

export type TPostChannelRecordingParams = {
  state: State;
  start_datetime: string | Date;
  segment_duration: number;
  channel_id: string;
  description?: string | null;
  end_datetime?: string | Date | null;
  production_centers: string[];
  schedule: TPostChannelRecordingSchedule[];
};

export async function postChannelRecording(
  body: TPostChannelRecordingParams
): Promise<ChannelRecordingType> {
  return mediaManagerClient.post('/api/v1/channel_recordings', emptyStringValues(body));
}

type MutationFn = typeof postChannelRecording;

type TUsePostChannelRecordingParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePostChannelRecording({ config = {} }: TUsePostChannelRecordingParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: postChannelRecording,
    onError(error) {
      addErrorNotification(t('No se ha podido crear la grabación'));
      console.error(error);
    },
    onSuccess(channelRecording, variables, context) {
      addSuccessNotification(t('La grabación se ha creado correctamente'));
      queryClient.invalidateQueries({ queryKey: channelRecordingsQueryKeys.all });
      config.onSuccess?.(channelRecording, variables, context);
    },
  });
}
